/**
 * Earnings Corrections API client (/api/admin/earnings-corrections).
 *
 * Reads need Financials access; every write also needs the
 * "Financials · Correct Earnings" permission. The server computes every
 * figure — the UI never prices a line itself.
 */

import { apiRequest } from './api';

const BASE = '/api/admin/earnings-corrections';

export type EarningsBucket = 'directSold' | 'adSense';
export type CorrectionKind = 'direct_sold_reprocess' | 'manual';
export type CorrectionStatus = 'draft' | 'applied' | 'reverted' | 'superseded' | 'discarded';
export type SettlementStatus = 'pending' | 'settled' | 'not_required';

export interface CorrectionActor {
  uid: string;
  email: string;
}

export interface CorrectionRowValues {
  grossUsd: number;
  earningsUsd: number;
  performance: number;
}

export interface CorrectionLine {
  month: string;
  bucket: EarningsBucket;
  userKey: string;
  userName: string;
  channelId: string;
  channelName: string;
  revenueSource: string;
  op: 'set' | 'remove';
  target: (CorrectionRowValues & { performanceMetric: string }) | null;
  base: CorrectionRowValues & { rowCount: number };
  note: string | null;
}

export interface CorrectionSourceMonth {
  month: string;
  uploadFileName: string | null;
  uploadedAt: string | null;
  uploadedBy: string | null;
  uploadContentHash: string | null;
  replacedPreviousAt: string | null;
  rateCardUpdatedAt: string | null;
  computedAt: string;
  flags: string[];
}

export interface CorrectionSummary {
  lineCount: number;
  netDeltaCents: { directSold: number; adSense: number; total: number };
  byMonth: Array<{ month: string; directSoldDeltaCents: number; adSenseDeltaCents: number }>;
}

export interface CorrectionSettlement {
  status: SettlementStatus;
  note: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface CorrectionHistoryEntry {
  at: string;
  by: string;
  action: 'created' | 'edited' | 'applied' | 'reverted' | 'superseded' | 'discarded' | 'settlement';
  note: string | null;
}

export interface EarningsCorrection {
  id: string;
  title: string;
  reason: string;
  kind: CorrectionKind;
  status: CorrectionStatus;
  months: string[];
  channelIds: string[];
  lines: CorrectionLine[];
  summary: CorrectionSummary;
  sources: CorrectionSourceMonth[];
  settlement: CorrectionSettlement;
  clientNote: string | null;
  createdBy: CorrectionActor;
  createdAt: string;
  updatedAt: string;
  appliedBy: CorrectionActor | null;
  appliedAt: string | null;
  revertedBy: CorrectionActor | null;
  revertedAt: string | null;
  revertReason: string | null;
  supersedes: string[];
  supersededBy: string | null;
  history: CorrectionHistoryEntry[];
}

/** List rows come without lines, history and bases. */
export type CorrectionListItem = Omit<EarningsCorrection, 'lines' | 'history' | 'sources'> & { sourceFlags: string[] };

export interface CorrectionLiveView {
  netDeltaCents: { directSold: number; adSense: number; total: number };
  lineCount: number;
  missingMonths: string[];
  unresolved: Array<{ rowKeyId: string; month: string; userName: string; channelName: string; revenueSource: string }>;
  drifted: Array<{
    rowKeyId: string;
    month: string;
    userName: string;
    channelName: string;
    revenueSource: string;
    base: CorrectionRowValues & { rowCount: number };
    now: CorrectionRowValues & { rowCount: number };
  }>;
  overlaps: Array<{ correctionId: string; rows: number }>;
  notOwned: number;
  stateVersion: number;
}

export interface CorrectionImpact {
  window: { fromMonth: string; toMonth: string };
  pages: Array<{
    slug: string;
    clientLabel: string;
    pageType: 'creator' | 'agency';
    includeAdSense: boolean;
    channels: number;
    months: string[];
    visible: boolean;
    hiddenReason?: 'outside_window' | 'adsense_hidden';
  }>;
  changes: string[];
  unchanged: string[];
}

export interface MonthRow {
  month: string;
  bucket: EarningsBucket;
  userKey: string;
  userName: string;
  channelId: string;
  channelName: string;
  revenueSource: string;
  values: CorrectionRowValues & { rowCount: number; performanceMetric: string | null };
  rowKeyId: string;
  ownerCorrectionId: string | null;
}

export interface CorrectionLineInput {
  month: string;
  bucket: EarningsBucket;
  userKey: string;
  channelId: string;
  channelName: string;
  revenueSource: string;
  op: 'set' | 'remove';
  target: { grossUsd: number; earningsUsd: number; performance: number; performanceMetric?: string | null } | null;
  note?: string | null;
}

export interface CorrectionDraftInput {
  title: string;
  reason: string;
  kind: CorrectionKind;
  lines: CorrectionLineInput[];
  sources?: CorrectionSourceMonth[];
  clientNote?: string | null;
  settlement?: { status: SettlementStatus; note?: string | null };
}

// ----- Direct Sold proposal -----

export type ProposalFlag =
  | 'assumed_split'
  | 'ambiguous_multi_user'
  | 'needs_user'
  | 'requires_review'
  | 'already_corrected'
  | 'matched_by_name'
  | 'unusual'
  | 'pre_existing';

export interface OwnerHint {
  userKey: string;
  userName: string;
  source: 'nearest_ds_month' | 'adsense_owner';
  month: string;
  inMonth: boolean;
}

export interface ProposalLine {
  month: string;
  channelId: string;
  channelName: string;
  gamNames: string[];
  userKey: string | null;
  userName: string | null;
  revenueSource: string;
  op: 'set' | 'remove';
  base: CorrectionRowValues & { rowCount: number };
  target: (CorrectionRowValues & { performanceMetric: string }) | null;
  deltaEarningsUsd: number;
  deltaGrossUsd: number;
  deltaImpressions: number;
  flags: ProposalFlag[];
  selected: boolean;
  ownerHints?: OwnerHint[];
  coveredBy?: string;
  input: CorrectionLineInput | null;
}

export interface ProposalMonth {
  month: string;
  scopeChannelIds: string[];
  lines: ProposalLine[];
  unchanged: Array<{ channelId: string; channelName: string; userName: string; earningsUsd: number; rounding?: boolean }>;
  unmapped: Array<{ gamName: string; grossUsd: number; payableUsd: number; impressions: number }>;
  undetailed: Array<{ channelId: string; channelName: string; userName: string; earningsUsd: number }>;
  control: {
    channels: number;
    summaryGrossUsd: number;
    cmGrossUsd: number;
    deltaGrossUsd: number;
    movers: Array<{ channelId: string; channelName: string; summaryGrossUsd: number; cmGrossUsd: number; deltaGrossUsd: number }>;
  };
  totals: { cmEarningsUsd: number; deltaEarningsUsd: number; selectedDeltaEarningsUsd: number };
  source: CorrectionSourceMonth & {
    anchor: {
      at: string | null;
      source: 'processing_run' | 'cm_upload' | 'cm_lock' | 'none';
      runId?: string;
      totalChannelEarningsUsd?: number;
      pipelineDeltaUsd?: number | null;
    };
  };
  userKeys: string[];
  warnings: string[];
}

export interface DsProposal {
  generatedAt: string;
  request: { months: string[]; user: string | null; channelIds: string[] };
  rateCardUpdatedAt: string | null;
  months: ProposalMonth[];
  totals: { lines: number; selectedLines: number; deltaEarningsUsd: number; selectedDeltaEarningsUsd: number };
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

export async function listEarningsCorrections(
  filters: { status?: CorrectionStatus; month?: string } = {},
): Promise<{ corrections: CorrectionListItem[]; version: number }> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.month) params.set('month', filters.month);
  const qs = params.toString();
  return apiRequest(`${BASE}${qs ? `?${qs}` : ''}`);
}

export async function getEarningsCorrection(id: string): Promise<{ correction: EarningsCorrection; live: CorrectionLiveView }> {
  return apiRequest(`${BASE}/${encodeURIComponent(id)}`);
}

export async function getEarningsCorrectionImpact(id: string): Promise<CorrectionImpact> {
  return apiRequest(`${BASE}/${encodeURIComponent(id)}/impact`);
}

export async function getCorrectableRows(
  month: string,
  opts: { q?: string; bucket?: EarningsBucket } = {},
): Promise<{ month: string; fetchedAt: string; total: number; truncated: boolean; rows: MonthRow[] }> {
  const params = new URLSearchParams();
  if (opts.q) params.set('q', opts.q);
  if (opts.bucket) params.set('bucket', opts.bucket);
  const qs = params.toString();
  return apiRequest(`${BASE}/months/${encodeURIComponent(month)}/rows${qs ? `?${qs}` : ''}`);
}

export async function proposeDirectSoldCorrections(body: {
  months: string[];
  user?: string;
  channelIds?: string[];
}): Promise<{ proposal: DsProposal }> {
  return apiRequest(`${BASE}/proposals/direct-sold`, { method: 'POST', body: JSON.stringify(body) });
}

export async function createEarningsCorrection(draft: CorrectionDraftInput): Promise<{ correction: EarningsCorrection }> {
  return apiRequest(BASE, { method: 'POST', body: JSON.stringify(draft) });
}

export async function updateEarningsCorrection(
  id: string,
  patch: Partial<Omit<CorrectionDraftInput, 'kind'>>,
): Promise<{ correction: EarningsCorrection }> {
  return apiRequest(`${BASE}/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function applyEarningsCorrection(
  id: string,
  body: { confirmNetDeltaCents: number; expectedLineCount: number; supersedes?: string[] },
): Promise<{ correction: EarningsCorrection; version: number; superseded: string[] }> {
  return apiRequest(`${BASE}/${encodeURIComponent(id)}/apply`, { method: 'POST', body: JSON.stringify(body) });
}

export async function revertEarningsCorrection(id: string, reason: string): Promise<{ correction: EarningsCorrection; version: number }> {
  return apiRequest(`${BASE}/${encodeURIComponent(id)}/revert`, { method: 'POST', body: JSON.stringify({ reason }) });
}

export async function discardEarningsCorrection(id: string): Promise<{ correction: EarningsCorrection }> {
  return apiRequest(`${BASE}/${encodeURIComponent(id)}/discard`, { method: 'POST', body: JSON.stringify({}) });
}

export async function updateEarningsCorrectionSettlement(
  id: string,
  body: { status: SettlementStatus; note?: string | null },
): Promise<{ correction: EarningsCorrection }> {
  return apiRequest(`${BASE}/${encodeURIComponent(id)}/settlement`, { method: 'PATCH', body: JSON.stringify(body) });
}
